import { useCallback, useEffect, useMemo, useState, type ComponentType } from "react";
import { useLocation } from "wouter";
import {
  ArrowRight,
  Building2,
  Car,
  ClipboardCheck,
  Package,
  Search,
  Settings2,
  UserRound,
  X,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useGlobalSearch } from "@/hooks/use-global-search";
import {
  buildGlobalSearchModel,
  type GlobalSearchGroup,
  type GlobalSearchResult,
} from "@/lib/global-search";

export interface SidebarSearchItem {
  label: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
}

export interface SidebarSearchProps {
  items: Array<SidebarSearchItem>;
  sidebarOpen: boolean;
  onNavigate?: () => void;
}

const GROUP_ICONS = {
  vehicles: Car,
  members: UserRound,
  properties: Building2,
  tasks: ClipboardCheck,
  supplies: Package,
  navigation: Settings2,
} as const;

const VISIBLE_GROUP_LIMIT = 9;

function statusTone(status?: string): string {
  const normalized = (status ?? "").toLowerCase().replace(/[_-]+/g, " ");
  if (["active", "current", "in residence", "open", "complete", "completed"].includes(normalized)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-800";
  }
  if (["moving in", "pending", "request", "requests", "booking request", "booking requested", "in progress"].includes(normalized)) {
    return "border-amber-200 bg-amber-50 text-amber-800";
  }
  return "border-slate-200 bg-slate-100 text-slate-600";
}

function seeAllHref(group: GlobalSearchGroup, query: string): string {
  const q = encodeURIComponent(query.trim());
  switch (group.key) {
    case "vehicles":
      return `/app/members/vehicles?q=${q}&status=all`;
    case "members":
      return `/app/members?q=${q}&status=all`;
    case "properties":
      return `/app/properties?q=${q}`;
    case "tasks":
      return `/app/tasks?q=${q}`;
    case "supplies":
      return `/app/supplies?tab=catalog&q=${q}`;
    case "navigation":
      return group.results[0]?.href ?? "/app/dashboard";
  }
}

function ResultLine({
  result,
  icon: Icon,
  onNavigate,
}: {
  result: GlobalSearchResult;
  icon: ComponentType<{ className?: string }>;
  onNavigate: (href: string) => void;
}) {
  return (
    <CommandItem
      key={result.id}
      value={result.id}
      onSelect={() => onNavigate(result.href)}
      className="min-h-[54px] cursor-pointer gap-3 rounded-lg px-3 py-2 transition-colors data-[selected=true]:bg-brand-muted data-[selected=true]:text-brand"
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-brand-muted text-brand">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold leading-5">{result.title}</span>
        {result.subtitle ? (
          <span className="block truncate text-[11px] leading-4 text-muted-foreground">{result.subtitle}</span>
        ) : null}
      </span>
      {result.status ? (
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold capitalize ${statusTone(result.status)}`}>
          {result.status.replace(/[_-]+/g, " ")}
        </span>
      ) : null}
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" aria-hidden="true" />
    </CommandItem>
  );
}

export function SidebarSearch({ items, sidebarOpen, onNavigate }: SidebarSearchProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [, navigate] = useLocation();
  const allowedHrefs = useMemo(() => new Set(items.map((item) => item.href)), [items]);
  const search = useGlobalSearch(open, query, allowedHrefs);
  const model = useMemo(
    () =>
      buildGlobalSearchModel({
        query,
        sources: search.sources,
        navigation: items,
        memberAccess: search.memberAccess,
        vehicleAccess: search.vehicleAccess,
      }),
    [items, query, search.memberAccess, search.sources, search.vehicleAccess],
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);
  const toggle = useCallback(() => {
    setOpen((current) => {
      if (current) setQuery("");
      return !current;
    });
  }, []);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [toggle]);

  const goTo = (href: string) => {
    close();
    onNavigate?.();
    navigate(href);
  };

  const isLoading = search.active && Object.values(search.states).some((source) => source.isLoading);
  const failedSources = Object.entries(search.states)
    .filter(([, source]) => source.error)
    .map(([key]) => key);
  const resultCount = model.groups.reduce((total, group) => total + group.total, 0);

  return (
    <>
      <button
        type="button"
        data-testid="button-sidebar-search"
        onClick={() => setOpen(true)}
        aria-label="Search navigation and account records"
        aria-keyshortcuts="Control+K Meta+K"
        title={!sidebarOpen ? "Search (Ctrl K)" : undefined}
        className={`group flex w-full items-center rounded-lg px-3 py-2.5 text-gray-600 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${sidebarOpen ? "gap-2" : "justify-center"}`}
      >
        <Search className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
        {sidebarOpen && (
          <>
            <span className="flex-1 text-left text-sm font-medium">Search</span>
            <kbd className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] leading-none text-gray-500">
              Ctrl K
            </kbd>
          </>
        )}
      </button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen) setOpen(true);
          else close();
        }}
      >
        <DialogContent className="max-h-[min(84dvh,46rem)] w-[calc(100vw-1.25rem)] max-w-[44rem] gap-0 overflow-hidden rounded-2xl border-border bg-popover p-0 shadow-lg sm:w-[calc(100vw-3rem)] [&>button]:hidden">
          <DialogTitle className="sr-only">Search navigation and account records</DialogTitle>
          <DialogDescription className="sr-only">
            Search members, vehicles, properties, maintenance tasks, supplies, and available destinations.
          </DialogDescription>
          <Command
            loop
            shouldFilter={false}
            className="max-h-[inherit] rounded-2xl bg-transparent text-popover-foreground"
            onKeyDown={(event) => {
              if (event.key === "Escape") close();
            }}
          >
            <div className="flex items-center border-b border-border bg-background px-4 [&_[cmdk-input-wrapper]]:min-w-0 [&_[cmdk-input-wrapper]]:flex-1 [&_[cmdk-input-wrapper]]:border-b-0 [&_[cmdk-input-wrapper]]:px-0">
              <CommandInput
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder="Search people, plates, addresses, tasks…"
                aria-label="Search account records and destinations"
                className="h-[62px] min-w-0 text-[15px] placeholder:text-muted-foreground"
              />
              {!query && (
                <kbd className="ml-2 hidden shrink-0 rounded border border-border bg-muted px-1.5 py-1 font-mono text-[10px] text-muted-foreground sm:block">
                  ESC
                </kbd>
              )}
              <button
                type="button"
                aria-label="Close search"
                onClick={close}
                className="ml-2 grid h-8 w-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <CommandList
              // ph-mask: search results name members/properties — masked in session replay.
              className="ph-mask max-h-[min(63dvh,36rem)] min-h-24 overflow-y-auto overscroll-contain px-3 py-3"
              aria-label="Search results"
            >
              {!query.trim() ? (
                <div className="px-3 pb-2 pt-1">
                  <p className="mb-2 px-2 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                    Available destinations
                  </p>
                  <CommandGroup heading="Navigation / Settings" className="p-0">
                    {items.map(({ label, href, icon: Icon }) => (
                      <CommandItem
                        key={`${href}:${label}`}
                        value={`${label} ${href}`}
                        onSelect={() => goTo(href)}
                        className="min-h-11 cursor-pointer rounded-lg px-3 py-2 transition-colors data-[selected=true]:bg-brand-muted data-[selected=true]:text-brand"
                      >
                        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-brand-muted text-brand">
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{label}</span>
                          <span className="block truncate text-[11px] text-muted-foreground">{href}</span>
                        </span>
                        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </div>
              ) : (
                <>
                  {isLoading ? (
                    <div role="status" aria-live="polite" className="space-y-2 px-2 py-2">
                      <span className="sr-only">Searching account records…</span>
                      {[0, 1, 2].map((line) => (
                        <div key={line} className="flex h-12 items-center gap-3 rounded-lg px-2">
                          <span className="h-8 w-8 animate-pulse rounded-md bg-muted" />
                          <span className="flex-1 space-y-2">
                            <span className="block h-2.5 w-2/5 animate-pulse rounded bg-muted" />
                            <span className="block h-2 w-3/5 animate-pulse rounded bg-muted/70" />
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {failedSources.length ? (
                    <div
                      role="alert"
                      className="mx-1 mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                    >
                      Some account records couldn’t be searched ({failedSources.join(", ")}). Other results are shown.
                    </div>
                  ) : null}
                  {model.groups.map((group) => {
                    const Icon = GROUP_ICONS[group.key];
                    const visible =
                      group.key === "navigation"
                        ? group.results
                        : group.results.slice(0, VISIBLE_GROUP_LIMIT);
                    return (
                      <CommandGroup
                        key={group.key}
                        heading={
                          <span className="flex items-center justify-between">
                            <span>{group.label}</span>
                            <span className="font-mono text-[10px] tabular-nums">{group.total}</span>
                          </span>
                        }
                        className="px-0 pb-2 pt-1"
                      >
                        {group.key === "vehicles" && model.vehicleIntent && model.vehicleHref ? (
                          <CommandItem
                            value={`vehicle-all:${query}`}
                            onSelect={() => goTo(model.vehicleHref!)}
                            className="mb-1 min-h-11 cursor-pointer rounded-lg border border-brand/20 bg-brand-muted px-3 py-2 font-semibold text-brand data-[selected=true]:bg-brand-muted"
                          >
                            <span className="grid h-7 w-7 place-items-center rounded-md bg-background text-brand">
                              <Car className="h-4 w-4" aria-hidden="true" />
                            </span>
                            <span className="min-w-0 flex-1 truncate">
                              {group.total > 0
                                ? `View ${group.total} vehicle${group.total === 1 ? "" : "s"} matching “${query.trim()}”`
                                : `Search vehicles for “${query.trim()}”`}
                            </span>
                            <ArrowRight className="h-4 w-4" aria-hidden="true" />
                          </CommandItem>
                        ) : null}
                        {visible.map((result) => (
                          <ResultLine
                            key={result.id}
                            result={result}
                            icon={Icon}
                            onNavigate={goTo}
                          />
                        ))}
                        {group.key !== "navigation" && group.total > VISIBLE_GROUP_LIMIT ? (
                          <CommandItem
                            value={`see-all:${group.key}:${query}`}
                            onSelect={() => goTo(seeAllHref(group, query))}
                            className="min-h-10 cursor-pointer justify-between rounded-lg px-3 text-xs font-semibold text-brand data-[selected=true]:bg-brand-muted"
                          >
                            See all {group.total} matching {String(group.label ?? "").toLowerCase()}
                            <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                          </CommandItem>
                        ) : null}
                      </CommandGroup>
                    );
                  })}
                  {!isLoading && !resultCount && !failedSources.length ? (
                    <CommandEmpty className="py-12 text-sm text-muted-foreground">
                      <span className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-full bg-brand-muted text-brand">
                        <Search className="h-4 w-4" aria-hidden="true" />
                      </span>
                      No matching records or destinations.
                    </CommandEmpty>
                  ) : null}
                  {!isLoading && !resultCount && failedSources.length ? (
                    <div className="px-4 py-7 text-center text-sm text-muted-foreground">
                      Search couldn’t reach one or more record sources. Try again in a moment.
                    </div>
                  ) : null}
                </>
              )}
            </CommandList>
            <div className="flex items-center justify-between border-t border-border bg-muted/40 px-4 py-2.5 text-[10px] text-muted-foreground">
              <span>{query.trim() ? `${resultCount} result${resultCount === 1 ? "" : "s"} across your account` : "Search account records and available pages"}</span>
              <span className="flex items-center gap-3">
                <span><kbd className="rounded border bg-white px-1 py-0.5 font-mono">↑</kbd> <kbd className="rounded border bg-white px-1 py-0.5 font-mono">↓</kbd> select</span>
                <span><kbd className="rounded border bg-white px-1 py-0.5 font-mono">↵</kbd> open</span>
              </span>
            </div>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}